// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React from 'react';
import './FeedbackResults.css';

const SENTIMENT_LABEL = {
  POSITIVE: { label: 'Positive', color: 'positive', icon: '😊' },
  NEGATIVE: { label: 'Critical', color: 'negative', icon: '😟' },
  MIXED: { label: 'Mixed', color: 'mixed', icon: '🤔' },
  NEUTRAL: { label: 'Neutral', color: 'neutral', icon: '😐' },
};

export default function FeedbackResults({ result, category, onStartOver }) {
  const sentiment = SENTIMENT_LABEL[result?.sentiment] || SENTIMENT_LABEL.NEUTRAL;
  const investmentAreas = result?.investmentAreas || [];
  const transcript = result?.transcript || '';
  const summary = result?.summary || '';

  return (
    <div className="feedback-results">
      <div className="feedback-results__success-banner" role="status" aria-live="polite">
        <span aria-hidden="true">✅</span>
        <span>Your feedback has been received — thank you!</span>
      </div>

      <div className="feedback-results__card">
        <div className="feedback-results__meta">
          <span className="feedback-results__category">
            <span aria-hidden="true">{category.icon}</span> {category.label}
          </span>
          <span className={`feedback-results__sentiment feedback-results__sentiment--${sentiment.color}`}>
            <span aria-hidden="true">{sentiment.icon}</span> {sentiment.label} Feedback
          </span>
        </div>

        {summary && (
          <section className="feedback-results__section" aria-label="Summary">
            <h3 className="feedback-results__section-title">Summary</h3>
            <p className="feedback-results__summary">{summary}</p>
          </section>
        )}

        {investmentAreas.length > 0 && (
          <section className="feedback-results__section" aria-label="Investment areas identified">
            <h3 className="feedback-results__section-title">Investment Areas Identified</h3>
            <ul className="feedback-results__areas" role="list">
              {investmentAreas.map((area, i) => (
                <li key={i} className="feedback-results__area-item">
                  <span className="feedback-results__area-icon" aria-hidden="true">🎯</span>
                  <div>
                    <strong className="feedback-results__area-name">{area.area}</strong>
                    {area.description && (
                      <p className="feedback-results__area-desc">{area.description}</p>
                    )}
                    {area.priority && (
                      <span className={`feedback-results__priority feedback-results__priority--${area.priority.toLowerCase()}`}>
                        {area.priority} priority
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {transcript && (
          <section className="feedback-results__section" aria-label="Transcript">
            <h3 className="feedback-results__section-title">
              Transcript
              <button
                className="feedback-results__toggle"
                onClick={(e) => {
                  const el = e.currentTarget.closest('section').querySelector('.feedback-results__transcript');
                  const isHidden = el.getAttribute('hidden') !== null;
                  if (isHidden) {
                    el.removeAttribute('hidden');
                    e.currentTarget.textContent = 'Hide';
                  } else {
                    el.setAttribute('hidden', '');
                    e.currentTarget.textContent = 'Show';
                  }
                }}
                aria-expanded="false"
                aria-controls="transcript-text"
              >
                Show
              </button>
            </h3>
            <p className="feedback-results__transcript" id="transcript-text" hidden>
              {transcript}
            </p>
          </section>
        )}

        <div className="feedback-results__id">
          Feedback ID: <code>{result?.feedbackId}</code>
        </div>
      </div>

      <div className="feedback-results__actions">
        <button className="btn btn--primary" onClick={onStartOver}>
          Submit More Feedback
        </button>
      </div>
    </div>
  );
}
